// An estimate of what marketplace health coverage costs a household between
// retiring and Medicare, year by year, from who is still under 65 (or, for a
// child, under 26), where they live and how old they are. The premium tax
// credit is not worked out here: it depends on each year's income, so the
// projection solves for it (`creditFor` in retirementPlan.js) from the
// benchmark premium this returns.
import { HEALTH_PREMIUMS } from '../constants/healthPremiums';

export const MEDICARE_START_AGE = 65;
// A child can stay on a parent's plan until this age.
export const CHILD_COVER_END_AGE = 26;
// Under this age a person is rated as a child, and only the oldest few in a family are charged.
const CHILD_RATED_BELOW = 21;
const OLDEST_BAND = 64;

const { ageCurve, stateCurves } = HEALTH_PREMIUMS;

const stateOf = (state) => (HEALTH_PREMIUMS.benchmark40[state] != null ? state : 'US');

/**
 * The premium of someone this age as a multiple of a 21-year-old's on the
 * federal scale, or on the state's own scale where it has one.
 */
export function ageFactor(age, state = 'US') {
  const own = stateCurves[state];
  const whole = Math.floor(age);
  if (whole < CHILD_RATED_BELOW) {
    if (own?.child != null) return own.child;
    return whole < 15 ? ageCurve.child : ageCurve.byAge[whole];
  }
  return (own?.adult || ageCurve.byAge)[Math.min(OLDEST_BAND, whole)];
}

/**
 * Full yearly price of the benchmark (second-cheapest silver) plan for these
 * people in this state, before any credit. `ages` are everyone to be covered.
 * The published figure is for a 40-year-old, so each person is scaled from it.
 */
export function benchmarkPremium(state, ages) {
  const where = stateOf(state);
  const monthlyAt40 = HEALTH_PREMIUMS.benchmark40[where];
  const flat = HEALTH_PREMIUMS.noAgeRating.includes(where);
  const at40 = ageFactor(40, where);
  const adults = ages.filter((age) => age >= CHILD_RATED_BELOW);
  // Only the oldest few children under 21 are charged a premium.
  const children = ages.filter((age) => age < CHILD_RATED_BELOW).sort((a, b) => b - a).slice(0, HEALTH_PREMIUMS.childrenCharged);
  // Where premiums do not vary with an adult's age, every adult pays the published price.
  const adultShare = adults.reduce((sum, age) => sum + (flat ? 1 : ageFactor(age, where) / at40), 0);
  const childShare = children.reduce((sum, age) => sum + ageFactor(age, where) / at40, 0);
  return 12 * monthlyAt40 * (adultShare + childShare);
}

/**
 * Who is on marketplace coverage in each year of retirement and what the
 * benchmark plan costs them. `people` are adults ({ age } today), `children`
 * ages today; `yearsToRetirement` and `years` place the retirement.
 */
export function estimateHealthCover({ state, people, children = [], yearsToRetirement, years }) {
  const premium = new Array(years).fill(0);
  const covered = new Array(years).fill(0);
  const household = new Array(years).fill(people.length);
  for (let y = 0; y < years; y++) {
    const ahead = yearsToRetirement + y;
    const adults = people.map((person) => person.age + ahead).filter((age) => age < MEDICARE_START_AGE);
    const kids = children.map((age) => age + ahead).filter((age) => age < CHILD_COVER_END_AGE);
    const ages = [...adults, ...kids];
    covered[y] = ages.length;
    // Children still on the plan are counted in the household the credit is worked out for.
    household[y] = people.length + kids.length;
    premium[y] = ages.length ? benchmarkPremium(state, ages) : 0;
  }
  return { premium, covered, household };
}

/** What the estimate rests on, and what it leaves out, in the words shown beside it. */
export function healthEstimateNotes(state) {
  const where = stateOf(state);
  const named = where !== 'US';
  const scale = HEALTH_PREMIUMS.noAgeRating.includes(where)
    ? 'Your state does not vary an adult\'s premium by age, so each adult is priced the same.'
    : `Each person is priced for their age on ${stateCurves[where] ? "your state's own age scale" : 'the federal age scale'}: at 64 a premium is about ${(ageFactor(64, where) / ageFactor(40, where)).toFixed(1)} times what it is at 40.`;
  const lines = [
    `Premiums start from the ${HEALTH_PREMIUMS.year} average price of the benchmark plan (the second-cheapest silver plan) ${named ? 'in your state' : 'across the country, because no state is chosen'}. ${scale}`,
    'The premium tax credit is worked out each year from that year\'s income and household size. It stops altogether above four times the poverty line, so a year with a large withdrawal or Roth conversion can lose all of it.',
    'Not included: premiums rising faster than other prices, differences between counties and between plans, tobacco surcharges, dental and vision coverage.',
  ];
  lines.push(HEALTH_PREMIUMS.medicaidNotExpanded.includes(where)
    ? 'No credit is paid in a year when income is below the poverty line. Your state has not widened Medicaid, so in such a year there is neither Medicaid nor a credit and the full price is paid.'
    : 'No credit is paid in a year when income is below the poverty line: the full price is counted, though in a state that has widened Medicaid you would be on Medicaid instead, which this estimate does not model.');
  return lines;
}
